import type { Emitter } from 'mitt'
import { Stack } from '@/plugins/cad/module/core/common/Stack'
import type { BaseTool } from './BaseTool'

/**
 * 工具栈，基于 Stack 扩展，负责工具切换时的生命周期调度与事件广播。
 */
export class ToolStack extends Stack<BaseTool> {
  /** 栈空时回落的默认工具 */
  defaultTool: BaseTool | null = null

  constructor(private emitter: Emitter<any>) {
    super()
  }

  /**
   * 将工具压入栈顶并广播工具切换事件。
   *
   * @param val - 待压入的工具
   */
  override push(val: BaseTool) {
    // 进栈，暂停上一次
    const last = this.top ?? this.defaultTool
    last?.onPause?.()

    super.push(val)
    this.emitter.emit('onToolChanged', {
      curTool: val,
      lastTool: last,
    })
  }

  /**
   * 弹出栈顶工具并触发其 terminate 生命周期。
   *
   * @returns 被弹出的工具；栈为空时返回 undefined
   */
  override pop() {
    // 出栈
    const item = super.pop()
    item?.onPause?.()
    ;(item as any)?.terminate()

    const last = this.top ?? this.defaultTool
    last?.onPlay?.()
    this.emitter.emit('onToolChanged', {
      curTool: last,
      lastTool: item,
    })

    return item
  }

  /**
   * 以独占方式启动工具，先清栈再入栈。
   *
   * @param val - 待独占入栈的工具
   */
  alone(val: BaseTool) {
    // 独享栈(清空栈后只保留当前)
    if (this.length === 0) {
      this.push(val)
      return
    }

    // 清空栈(清空时不触发 onToolChanged 事件)，一次操作只触发一次 onToolChanged 事件
    this.clear(false)
    this.push(val)
  }

  /**
   * 清空工具栈，可选择是否广播工具切换事件。
   *
   * @param isEmit - 是否在清空后广播 onToolChanged，默认 true
   */
  override clear(isEmit: boolean = true) {
    const last = this.top ?? this.defaultTool
    // 清空栈(清空时退出所有)
    this.forEach((tool) => {
      ;(tool as any)?.terminate()
    })
    super.clear()
    if (isEmit) {
      this.emitter.emit('onToolChanged', {
        curTool: null,
        lastTool: last,
      })
    }
  }
}
